import { Drawer, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { useQuery } from '@tanstack/react-query';
import {
  INVOICE_CHECK_LABELS,
  describeInvoiceIssue,
  type InvoiceCheckIssue,
  cancelReasonLabel,
  dailyClassAdjustmentLabel,
  formatDateDDMMYYYY,
  hebrewDayOfWeek,
  formatWithholdingPct,
  meetingClassLabel,
  deductionLabel,
  oneTimeBonusLabel,
  volumeBonusLabel,
  conversionBonusLabel,
  conversionTierText,
  withholdingOf,
  hugCoursePay,
  manualSpreadLabel,
  type CancelledMeetingDetail,
  type ConversionDetail,
  type HugLineDetail,
  type InvoiceCheckStatus,
  type ManualAdjustmentLine,
  type MeetingDetail,
  type PeriodSelection,
} from '@salaries/shared';
import { getPayrollDetail } from '../../api/payroll';
import { Money, SignedMoney } from '../../components/Money';
import { Loader } from '../../components/Loader';
import { EmptyState } from '../../components/EmptyState';
import { columnsWidth, numSorter, textSorter, valueFilter } from '../../components/tableSortFilter';

// Table headers stay put while the drawer scrolls. The drawer body is the scroll
// container and has 24px padding; without the offset, rows would show through
// that strip above the frozen header.
const STICKY_IN_DRAWER = { offsetHeader: -24 };

type AttendanceRow = { date: string; day_of_week: string; status: string };

// Read-only breakdown of a staff member's salary for the period — what the owner
// reviews before sending to the coach. Opened from the row's "הצג פירוט" action.
export function PayrollDetailDrawer({
  staffId,
  staffName,
  selection,
  unitId,
  unitName,
  invoiceCheckStatus,
  invoiceAmount,
  invoiceCheckIssues = [],
  onClose,
}: {
  staffId: number | null;
  staffName: string;
  selection: PeriodSelection;
  /** P&L-unit slice to show (undefined when the box doesn't split by unit). */
  unitId?: number;
  unitName?: string | null;
  /** Verdict of the AI invoice-amount check; null when no check has run. */
  invoiceCheckStatus?: InvoiceCheckStatus | null;
  /** The total read off the coach's uploaded invoice. */
  invoiceAmount?: number | null;
  /** Which invoice checks failed, listed under the verdict. */
  invoiceCheckIssues?: InvoiceCheckIssue[];
  onClose: () => void;
}) {
  const open = staffId !== null;
  const detailQuery = useQuery({
    queryKey: ['payroll-detail', staffId, selection, unitId],
    queryFn: () => getPayrollDetail(staffId!, selection, unitId),
    enabled: open,
  });
  const detail = detailQuery.data;
  const comp = detail?.computation;
  const hugim = detail?.hugim;
  const timeClock = detail?.time_clock;
  // `mechanisms` is the definitive signal (set on every freshly-built detail);
  // fall back to inferring from block presence only for a locked snapshot
  // frozen before this field existed — back then group_classes could never
  // combine with anything, so "neither hugim nor time_clock" was always
  // exactly the group_classes case.
  //
  // A group_classes+time_clock staff member (a coach who also works שעון
  // נוכחות shifts) does NOT get the rich per-meeting table here — only the
  // combined totals below — so this must check for group_classes ALONE, not
  // just "hugim/time_clock are absent".
  const isPureGroupClasses = detail?.mechanisms
    ? detail.mechanisms.length === 1 && detail.mechanisms[0] === 'group_classes'
    : !hugim && !timeClock;
  const mechanismLabel = (m: string) =>
    m === 'group_classes' ? 'שיעורים קבוצתיים' : m === 'hugim' ? 'חוגים' : 'שעון נוכחות';

  // Location only matters when the box teaches at more than one location; the
  // per-class bonus column only when this coach actually earns one.
  const showLocation = !!detail?.multi_location;
  const showMeetingBonus = (comp?.class_bonus ?? 0) > 0;
  // `!== 0`, not `> 0`: this coach's daily class-count adjustment can be a pure
  // deduction, and the column must not hide itself in that case.
  const showDailyAdjustment = (comp?.daily_class_adjustment ?? 0) !== 0;

  // Every table below sorts on every column and filters on the columns worth
  // filtering by (values present in its own rows only). Its summary row sums
  // what's left after filtering. Every column carries a width: the headers are
  // sticky, and a wide table scrolls sideways rather than clipping a column.
  const dateCell = (v: string | null) => <span dir="ltr">{v ? formatDateDDMMYYYY(v) : '—'}</span>;
  const meetings = detail?.meetings ?? [];
  const meetingColumns: ColumnsType<MeetingDetail> = [
    { title: 'תאריך', dataIndex: 'date', width: 110, render: dateCell, sorter: textSorter((m) => m.date) },
    {
      title: 'יום',
      dataIndex: 'day_of_week',
      width: 80,
      render: (v) => v || '—',
      sorter: textSorter((m) => m.date),
      ...valueFilter(meetings, (m) => m.day_of_week),
    },
    {
      title: 'שעה',
      dataIndex: 'time',
      width: 80,
      render: (v) => v || '—',
      sorter: textSorter((m) => m.time),
      ...valueFilter(meetings, (m) => m.time),
    },
    ...(showLocation
      ? ([
          {
            title: 'מיקום',
            dataIndex: 'location',
            width: 120,
            render: (v: string | null) => v || '—',
            sorter: textSorter<MeetingDetail>((m) => m.location),
            ...valueFilter(meetings, (m) => m.location),
          },
        ] as ColumnsType<MeetingDetail>)
      : []),
    {
      title: 'שיעור',
      dataIndex: 'class_name',
      width: 180,
      // Co-taught classes note the other coach: the ₪ here is this coach's own
      // share, not the meeting's full cost.
      render: (_v, m) => meetingClassLabel(m),
      sorter: textSorter((m) => m.class_name),
      ...valueFilter(meetings, (m) => m.class_name),
    },
    { title: 'רשומים', dataIndex: 'registration_count', width: 90, align: 'right', sorter: numSorter((m) => m.registration_count) },
    { title: "צ'ק-אין", dataIndex: 'checkin_count', width: 90, align: 'right', sorter: numSorter((m) => m.checkin_count) },
    { title: 'ביטולים', dataIndex: 'late_cancel_count', width: 90, align: 'right', sorter: numSorter((m) => m.late_cancel_count) },
    {
      title: 'שכר',
      dataIndex: 'salary',
      width: 100,
      align: 'right',
      sorter: numSorter((m) => m.salary),
      // An empty class pays the flat empty-class amount; otherwise our series /
      // קטגוריה rate may have replaced Arbox's figure — say so, with what Arbox had.
      render: (v, m) =>
        m.empty_class ? (
          <Tooltip title={`שיעור ריק — שולם תעריף שיעור ריק. בלי זה: ₪${m.full_salary ?? 0}`}>
            <span className="underline decoration-dotted">
              <Money value={v} />
            </span>
          </Tooltip>
        ) : m.rate_source ? (
          <Tooltip
            title={`${m.rate_source === 'series' ? 'תעריף לפי שיעור קבוע' : m.rate_source === 'branch' ? `תעריף לפי סניף (${m.location ?? ''})` : `תעריף לפי קטגוריה (${m.box_category ?? ''})`}. בארבוקס: ₪${m.arbox_salary ?? 0}`}
          >
            <span className="underline decoration-dotted">
              <Money value={v} />
            </span>
          </Tooltip>
        ) : (
          <Money value={v} />
        ),
    },
    ...(showMeetingBonus
      ? [{
          title: 'תוספת שיעור',
          dataIndex: 'class_bonus',
          width: 110,
          align: 'right' as const,
          sorter: numSorter<MeetingDetail>((m) => m.class_bonus),
          render: (v: number) =>
            v > 0 ? <Money value={v} /> : <span className="text-secondary-text">—</span>,
        }]
      : []),
    ...(showDailyAdjustment
      ? [
          {
            title: 'שיעורים ביום',
            dataIndex: 'daily_class_count',
            width: 110,
            align: 'right' as const,
            sorter: numSorter<MeetingDetail>((m) => m.daily_class_count),
            render: (v: number) => v || <span className="text-secondary-text">—</span>,
          },
          {
            title: 'התאמה יומית',
            dataIndex: 'daily_class_adjustment',
            width: 110,
            align: 'right' as const,
            sorter: numSorter<MeetingDetail>((m) => m.daily_class_adjustment),
            render: (v: number) =>
              v !== 0 ? <Money value={v} /> : <span className="text-secondary-text">—</span>,
          },
        ]
      : []),
  ];
  // Columns left of "שכר": date/day/time + class/registered/checkin/cancel (=7),
  // plus the optional location column.
  const meetingLeadCols = 7 + (showLocation ? 1 : 0);

  // Hugim breakdown: one row per receipt recognized this month. Each receipt is
  // spread evenly from the month it was collected to the hug's last month, so
  // "עבור החודש" is this month's slice of "סכום הקבלה". Built per table, so each
  // table's filters offer only its own rows' values.
  const hasAddons = (hugim?.lines ?? []).some((l) => (l.addons_payout ?? 0) !== 0);
  // איש צוות משני: a role column only when this staff member is the second on
  // some receipt — the same course can then appear once per role.
  const hasRoles = (hugim?.lines ?? []).some((l) => l.role === 'secondary');
  const roleLabel = (l: HugLineDetail) => (l.role === 'secondary' ? 'משני' : 'ראשי');
  const hugColumnsFor = (rows: HugLineDetail[]): ColumnsType<HugLineDetail> => [
    {
      title: 'חוג',
      dataIndex: 'hug_name',
      width: 145,
      // החלפת חוג: marked on both the old course's staff and the new one's, in
      // every month the routing touches — the month resolution can hand the new
      // staff days the old one still taught, and the bonus column is the fix.
      // מפגש ניסיון: a paid trial session — paid like any receipt, tagged so
      // it can be told apart from a course payment.
      render: (v, l) => {
        const name = l.swapped ? (
          <Tooltip title="שים לב, החוג הוחלף. אם ברצונך לבצע תיקונים ידניים ניתן לעשות זאת דרך עמודת הבונוס החד-פעמי בדוח השכר.">
            <span>
              {v || '—'}
              <span className="text-warning-yellow ms-1">*</span>
            </span>
          </Tooltip>
        ) : (
          v || '—'
        );
        // מפגש חד-פעמי: same idea — a paid one-time session, tagged.
        const tag = l.trial ? 'ניסיון' : l.onetime ? 'חד-פעמי' : null;
        return tag ? (
          <span>
            {name}
            <Tag color="default" className="ms-1 me-0">
              {tag}
            </Tag>
          </span>
        ) : (
          name
        );
      },
      sorter: textSorter((l) => l.hug_name),
      ...valueFilter(rows, (l) => l.hug_name),
    },
    ...(hasRoles
      ? ([
          {
            title: 'תפקיד',
            key: 'role',
            width: 80,
            render: (_: unknown, l: HugLineDetail) => roleLabel(l),
            sorter: textSorter(roleLabel),
            ...valueFilter(rows, roleLabel),
          },
        ] as ColumnsType<HugLineDetail>)
      : []),
    {
      title: 'משתתף',
      dataIndex: 'participant_name',
      width: 110,
      render: (v) => v || '—',
      sorter: textSorter((l) => l.participant_name),
      ...valueFilter(rows, (l) => l.participant_name),
    },
    { title: 'תאריך קבלה', dataIndex: 'receipt_date', width: 110, render: dateCell, sorter: textSorter((l) => l.receipt_date) },
    {
      title: 'סכום הקבלה',
      dataIndex: 'total_paid',
      width: 110,
      align: 'right',
      sorter: numSorter((l) => l.total_paid),
      render: (v) => <Money value={v} />,
    },
    {
      title: 'חודשי פריסה',
      dataIndex: 'spread_months',
      width: 100,
      align: 'right',
      sorter: numSorter((l) => l.spread_months),
      render: (v: number) => (
        <span title="מספר החודשים שסכום הקבלה נפרס עליהם — מחודש הקבלה ועד סוף החוג">{v}</span>
      ),
    },
    {
      title: 'עבור החודש',
      dataIndex: 'revenue',
      width: 110,
      align: 'right',
      sorter: numSorter((l) => l.revenue),
      render: (v) => <Money value={v} />,
    },
    {
      title: 'אחוז',
      dataIndex: 'percent',
      width: 80,
      align: 'right',
      render: (v: number) => `${v}%`,
      sorter: numSorter((l) => l.percent),
      ...valueFilter(rows, (l) => l.percent, (v) => `${v}%`),
    },
    { title: 'שכר', dataIndex: 'amount', width: 100, align: 'right', render: (v) => <Money value={v} />, sorter: numSorter((l) => l.amount) },
    // מוצרים נלווים — only when some receipt this month actually pays on them.
    // Empty on a receipt with no add-ons or no percentage set for them.
    ...(hasAddons
      ? ([
          {
            title: 'תשלום על נלווים',
            dataIndex: 'addons_payout',
            width: 120,
            align: 'right',
            sorter: numSorter<HugLineDetail>((l) => l.addons_payout),
            render: (v: number | undefined, l: HugLineDetail) =>
              v ? (
                <span
                  title={`${l.addons_label ?? ''} · ${l.addons_percent ?? 0}% מ-${l.addons_revenue ?? 0} ₪ החודש`}
                >
                  <Money value={v} />
                </span>
              ) : (
                <span className="text-secondary-text">—</span>
              ),
          },
        ] as ColumnsType<HugLineDetail>)
      : []),
  ];

  // Cancelled classes the box still paid for, with the reason that set the rate.
  const cancelledMeetings = detail?.cancelled_meetings ?? [];
  const cancelledColumns: ColumnsType<CancelledMeetingDetail> = [
    { title: 'תאריך', dataIndex: 'date', width: 110, render: dateCell, sorter: textSorter((m) => m.date) },
    {
      title: 'יום',
      dataIndex: 'day_of_week',
      width: 80,
      render: (v) => v || '—',
      sorter: textSorter((m) => m.date),
      ...valueFilter(cancelledMeetings, (m) => m.day_of_week),
    },
    { title: 'שעה', dataIndex: 'time', width: 80, render: (v) => v || '—', sorter: textSorter((m) => m.time) },
    ...(showLocation
      ? ([
          {
            title: 'מיקום',
            dataIndex: 'location',
            width: 120,
            render: (v: string | null) => v || '—',
            sorter: textSorter<CancelledMeetingDetail>((m) => m.location),
            ...valueFilter(cancelledMeetings, (m) => m.location),
          },
        ] as ColumnsType<CancelledMeetingDetail>)
      : []),
    {
      title: 'שיעור',
      dataIndex: 'class_name',
      width: 160,
      render: (v) => v || '—',
      sorter: textSorter((m) => m.class_name),
      ...valueFilter(cancelledMeetings, (m) => m.class_name),
    },
    {
      title: 'סיבת ביטול',
      dataIndex: 'cancel_reason',
      width: 140,
      render: (v: string | null) => cancelReasonLabel(v),
      sorter: textSorter((m) => cancelReasonLabel(m.cancel_reason)),
      ...valueFilter(cancelledMeetings, (m) => cancelReasonLabel(m.cancel_reason)),
    },
    { title: 'שכר מלא', dataIndex: 'full_salary', width: 100, align: 'right', render: (v: number) => <Money value={v} />, sorter: numSorter((m) => m.full_salary) },
    {
      title: 'אחוז',
      dataIndex: 'percent',
      width: 80,
      align: 'right',
      render: (v: number) => `${v}%`,
      sorter: numSorter((m) => m.percent),
      ...valueFilter(cancelledMeetings, (m) => m.percent, (v) => `${v}%`),
    },
    { title: 'תשלום', dataIndex: 'salary', width: 100, align: 'right', render: (v: number) => <Money value={v} />, sorter: numSorter((m) => m.salary) },
  ];

  // Converted trial leads that earned a conversion bonus this period.
  const conversions = detail?.conversions ?? [];
  const conversionColumns: ColumnsType<ConversionDetail> = [
    { title: 'תאריך ניסיון', dataIndex: 'trial_date', width: 110, render: dateCell, sorter: textSorter((c) => c.trial_date) },
    ...(showLocation
      ? ([
          {
            title: 'מיקום',
            dataIndex: 'location',
            width: 120,
            render: (v: string | null) => v || '—',
            sorter: textSorter<ConversionDetail>((c) => c.location),
            ...valueFilter(conversions, (c) => c.location),
          },
        ] as ColumnsType<ConversionDetail>)
      : []),
    {
      title: 'שיעור',
      dataIndex: 'class_name',
      width: 160,
      render: (v) => v || '—',
      sorter: textSorter((c) => c.class_name),
      ...valueFilter(conversions, (c) => c.class_name),
    },
    { title: 'ליד', dataIndex: 'lead_name', width: 140, render: (v) => v || '—', sorter: textSorter((c) => c.lead_name) },
    { title: 'ימים להמרה', dataIndex: 'days_to_convert', width: 110, align: 'right', render: (v) => v ?? '—', sorter: numSorter((c) => c.days_to_convert) },
    { title: 'בונוס', dataIndex: 'bonus', width: 100, align: 'right', render: (v: number) => <Money value={v} />, sorter: numSorter((c) => c.bonus) },
  ];

  // Attendance days (שעון נוכחות), one row per day. A day with a clock-in but
  // no clock-out still counts as a workday, but its hours were dropped.
  const incompleteDays = new Set(timeClock?.incomplete_days ?? []);
  const attendanceRows: AttendanceRow[] = [
    ...new Set([...(timeClock?.attendance_days ?? []), ...(timeClock?.incomplete_days ?? [])]),
  ]
    .sort()
    .map((date) => ({
      date,
      day_of_week: hebrewDayOfWeek(date) ?? '—',
      status: incompleteDays.has(date) ? 'חסרה שעת יציאה' : 'תקין',
    }));
  const attendanceColumns: ColumnsType<AttendanceRow> = [
    { title: 'תאריך', dataIndex: 'date', width: 120, render: dateCell, sorter: textSorter((r) => r.date), defaultSortOrder: 'ascend' },
    {
      title: 'יום',
      dataIndex: 'day_of_week',
      width: 100,
      sorter: textSorter((r) => r.date),
      ...valueFilter(attendanceRows, (r) => r.day_of_week),
    },
    {
      title: 'סטטוס',
      dataIndex: 'status',
      width: 160,
      render: (v: string) =>
        v === 'תקין' ? (
          v
        ) : (
          <span className="text-warning-yellow">
            <FontAwesomeIcon icon={faTriangleExclamation} /> {v}
          </span>
        ),
      sorter: textSorter((r) => r.status),
      ...valueFilter(attendanceRows, (r) => r.status),
    },
  ];

  // חוג לכל ילד lines are reported in their own table (same columns), because on
  // them "סכום הקבלה" is the חוג's full price rather than what the family paid.
  // Lines frozen before the feature carry no flag, which is exactly "not one".
  const regularHugLines = (hugim?.lines ?? []).filter((l) => !l.per_child_fee);
  const perChildFeeLines = (hugim?.lines ?? []).filter((l) => l.per_child_fee);

  const manualLines = hugim?.computation.manual_lines ?? [];
  const manualNet = (hugim?.computation.manual_additions ?? 0) - (hugim?.computation.manual_offsets ?? 0);
  const manualColumns: ColumnsType<ManualAdjustmentLine> = [
    { title: 'תיאור', dataIndex: 'description' },
    {
      title: 'סכום',
      dataIndex: 'amount',
      width: 140,
      align: 'right',
      render: (v: number, l) => <SignedMoney value={l.kind === 'offset' ? -v : v} />,
    },
    { title: 'פריסה', key: 'spread', width: 140, render: (_: unknown, l) => manualSpreadLabel(l) },
  ];

  const renderHugTable = (title: string, rows: HugLineDetail[], emptyText: string) => {
    const cols = hugColumnsFor(rows);
    const roleCol = hasRoles ? 1 : 0;
    return (
    <div>
      <div className="font-semibold mb-2">{title}</div>
      <Table<HugLineDetail>
        rowKey={(_, i) => String(i)}
        size="small"
        columns={cols}
        dataSource={rows}
        sticky={STICKY_IN_DRAWER}
        scroll={{ x: columnsWidth(cols) }}
        pagination={false}
        locale={{ emptyText }}
        summary={(r) =>
          r.length ? (
            <Table.Summary fixed>
              <Table.Summary.Row style={{ background: '#f7f7f7', fontWeight: 600 }}>
                <Table.Summary.Cell index={0} colSpan={3 + roleCol}>
                  סיכום
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3 + roleCol} align="right">
                  <Money value={r.reduce((a, x) => a + x.total_paid, 0)} />
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4 + roleCol} />
                <Table.Summary.Cell index={5 + roleCol} align="right">
                  <Money value={r.reduce((a, x) => a + x.revenue, 0)} />
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6 + roleCol} />
                <Table.Summary.Cell index={7 + roleCol} align="right">
                  <Money value={r.reduce((a, x) => a + x.amount, 0)} />
                </Table.Summary.Cell>
                {hasAddons && (
                  <Table.Summary.Cell index={8 + roleCol} align="right">
                    <Money value={r.reduce((a, x) => a + (x.addons_payout ?? 0), 0)} />
                  </Table.Summary.Cell>
                )}
              </Table.Summary.Row>
            </Table.Summary>
          ) : null
        }
      />
    </div>
    );
  };

  // Component lines — a UNION, not one mechanism's exclusive list: a
  // multi-mechanism staff member (e.g. hugim + time_clock) needs BOTH the
  // hugim lines AND the generic components (base_salary/travel come from
  // time_clock in that case). The generic list is always included and then
  // filtered to non-zero — safe for hugim-only too, since every one of those
  // fields is 0 there. `comp` (not `hugim.computation`) is the single source
  // for the bonus: it always already carries the correctly-combined amount,
  // computed once against the full pre-bonus total regardless of mechanism.
  //
  // חוג לכל ילד is split OUT of שכר חוגים rather than added to it —
  // per_child_fee_pay is a subset of hug_pay, so the two lines sum back to the
  // hugim total exactly.
  const componentLines: { label: string; value: number }[] = [
    ...(hugim
      ? [
          {
            label: 'שכר חוגים',
            value: hugCoursePay(hugim.computation) - (hugim.computation.per_child_fee_pay ?? 0),
          },
          { label: 'חוג לכל ילד', value: hugim.computation.per_child_fee_pay ?? 0 },
          // Also subsets of hug_pay, split out the same way.
          { label: 'תשלום על נלווים', value: hugim.computation.addons_pay ?? 0 },
          { label: 'תוספות וקיזוזים', value: manualNet },
        ]
      : []),
    ...(comp
      ? [
          { label: 'שכר שיעורים', value: comp.class_pay },
          { label: 'תוספת סופ"ש', value: comp.weekend_bonus },
          { label: 'תוספת שיעור', value: comp.class_bonus },
          { label: volumeBonusLabel(comp.class_count), value: comp.volume_bonus ?? 0 },
          { label: dailyClassAdjustmentLabel(), value: comp.daily_class_adjustment ?? 0 },
          {
            label: conversionBonusLabel(comp.conversion_count, comp.conversion_bonus),
            value: comp.conversion_bonus,
          },
          { label: 'שכר בסיס', value: comp.base_salary },
          { label: 'נסיעות', value: comp.travel },
          { label: 'שיעורים ריקים', value: comp.empty_class_pay ?? 0 },
          { label: 'שיעורים שבוטלו', value: comp.cancelled_class_pay ?? 0 },
          {
            label: oneTimeBonusLabel(comp.one_time_bonus_percent, comp.one_time_bonus_note),
            value: comp.one_time_bonus,
          },
          // Stored positive, shown NEGATIVE with the reason the operator gave —
          // this is the line the coach will ask about, so it says what it was
          // for. Absent on snapshots frozen before the feature.
          {
            label: deductionLabel(comp.deduction_percent, comp.deduction_note),
            value: -(comp.deduction ?? 0),
          },
        ]
      : []),
  ].filter((l) => l.value !== 0);
  // `!== 0`: an offset larger than the month's pay makes the VAT negative too.
  if (comp && comp.vat !== 0) componentLines.push({ label: 'מע"מ', value: comp.vat });
  // ניכוי מס במקור sits below the invoice total, so the bottom line becomes what
  // actually gets transferred. Snapshots frozen before the feature read as none.
  const wh = comp ? withholdingOf(comp) : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      // Fits the hugim table even with its add-ons column; a group-classes table
      // with every optional column scrolls sideways.
      width="min(1040px, 100vw)"
      placement="left"
      title={
        <span className="text-heading-1 text-main-text">
          {`פירוט שכר — ${staffName}${unitName ? ` · ${unitName}` : ''}`}
        </span>
      }
      closable={false}
      extra={
        <button
          type="button"
          aria-label="סגור"
          onClick={onClose}
          className="flex items-center justify-center w-8 h-8 rounded text-secondary-text hover:bg-grey-bg hover:text-main-text text-2xl leading-none"
        >
          ×
        </button>
      }
      styles={{ header: { borderBottom: 'none' } }}
      destroyOnClose
    >
      {detailQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <Loader size={48} />
        </div>
      ) : detailQuery.isError || !detail ? (
        <EmptyState title="שגיאה בטעינת הפירוט" description="לא ניתן היה לטעון את פירוט השכר." />
      ) : (
        <div className="flex flex-col gap-5">
          <div className="text-small text-secondary-text">
            <div>תקופה: {detail.period.label}</div>
            {detail.employee.email && <div>{detail.employee.email}</div>}
            {/* Explicit, not inferred — a staff member combining mechanisms
                (e.g. שיעורים קבוצתיים + שעון נוכחות) should never leave the
                viewer guessing which numbers below come from where. */}
            {detail.mechanisms && detail.mechanisms.length > 0 && (
              <div>מנגנוני תשלום: {detail.mechanisms.map(mechanismLabel).join(' + ')}</div>
            )}
            {detail.global_salary && (
              <div>שכר גלובלי: סכום קבוע בכל תקופת שכר, ללא קשר לפעילות</div>
            )}
          </div>

          {/* Additive — a multi-mechanism staff member (e.g. hugim + time_clock,
              or group_classes + time_clock) shows every applicable breakdown,
              not one exclusive block. The rich per-meeting table below only
              appears for a staff member on group_classes ALONE. */}
          {hugim && (
            <>
              {/* The ordinary table is dropped only when it is empty AND the
                  חוג-לכל-ילד one isn't — otherwise a coach paid solely through the
                  subsidy reads "אין הכנסות מחוגים בתקופה" directly above his own
                  חוג income. A coach with nothing at all still gets that message. */}
              {(regularHugLines.length > 0 || perChildFeeLines.length === 0) &&
                renderHugTable('חוגים בתקופה', regularHugLines, 'אין הכנסות מחוגים בתקופה')}
              {/* חוג לכל ילד gets its own table: the money there is a percentage of
                  the חוג's FULL price, not of what the family paid, so mixing the
                  two under one "סכום הקבלה" heading would read as an error. Only
                  rendered when the box actually has such lines. */}
              {perChildFeeLines.length > 0 &&
                renderHugTable('חוג לכל ילד', perChildFeeLines, '')}
              {/* תוספות וקיזוזים — typed by the box, not from receipts. Not
                  shown at all in a month without any. */}
              {manualLines.length > 0 && (
                <div>
                  <div className="font-semibold mb-2">תוספות וקיזוזים</div>
                  <Table<ManualAdjustmentLine>
                    rowKey={(l) => `${l.kind}-${l.id}`}
                    size="small"
                    columns={manualColumns}
                    dataSource={manualLines}
                    pagination={false}
                    summary={() => (
                      <Table.Summary>
                        <Table.Summary.Row style={{ background: '#f7f7f7', fontWeight: 600 }}>
                          <Table.Summary.Cell index={0}>סה"כ תוספות וקיזוזים</Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="right">
                            <SignedMoney value={manualNet} />
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} />
                        </Table.Summary.Row>
                      </Table.Summary>
                    )}
                  />
                </div>
              )}
            </>
          )}


          {timeClock && (
            <div>
              <div className="font-semibold mb-2">נוכחות בתקופה</div>
              <div className="border rounded">
                <div className="flex justify-between px-3 py-2 border-b">
                  <span>ימי נוכחות</span>
                  <span>{timeClock.attendance_days.length}</span>
                </div>
                {timeClock.base_mode === 'hourly' && (
                  <div className="flex justify-between px-3 py-2 border-b">
                    <span>שעות שנצברו</span>
                    <span dir="ltr">{timeClock.clocked_hours}</span>
                  </div>
                )}
              </div>
              <Table<AttendanceRow>
                className="mt-2"
                rowKey="date"
                size="small"
                columns={attendanceColumns}
                dataSource={attendanceRows}
                pagination={false}
                sticky={STICKY_IN_DRAWER}
                scroll={{ x: columnsWidth(attendanceColumns) }}
                locale={{ emptyText: 'אין ימי נוכחות בתקופה' }}
              />
              {timeClock.incomplete_days.length > 0 && (
                <div className="border border-warning-yellow rounded mt-2 px-3 py-2 text-small text-warning-yellow">
                  <div className="flex items-center gap-1.5 font-medium">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    <span>
                      {timeClock.incomplete_days.length === 1
                        ? 'יום אחד עם שעת יציאה חסרה'
                        : `${timeClock.incomplete_days.length} ימים עם שעת יציאה חסרה`}
                    </span>
                  </div>
                  <div className="mt-1 text-secondary-text">
                    {timeClock.base_mode === 'hourly'
                      ? 'לא ניתן היה לחשב שעות עבור ימים אלה — ייתכן שהשכר לתקופה זו חסר.'
                      : 'לא נקלטה שעת יציאה עבור ימים אלה בדוח הנוכחות.'}
                  </div>
                </div>
              )}
            </div>
          )}

          {isPureGroupClasses && (
            <div>
              <div className="font-semibold mb-2">שיעורים בתקופה</div>
              <Table<MeetingDetail>
                rowKey={(_, i) => String(i)}
                size="small"
                columns={meetingColumns}
                dataSource={meetings}
                sticky={STICKY_IN_DRAWER}
                scroll={{ x: columnsWidth(meetingColumns) }}
                pagination={false}
                locale={{ emptyText: 'אין שיעורים בתקופה' }}
                summary={(rows) =>
                  rows.length ? (
                    <Table.Summary fixed>
                      <Table.Summary.Row style={{ background: '#f7f7f7', fontWeight: 600 }}>
                        <Table.Summary.Cell index={0} colSpan={meetingLeadCols}>
                          סיכום שיעורים
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <Money value={rows.reduce((a, r) => a + r.salary, 0)} />
                        </Table.Summary.Cell>
                        {showMeetingBonus && (
                          <Table.Summary.Cell index={2} align="right">
                            <Money value={rows.reduce((a, r) => a + r.class_bonus, 0)} />
                          </Table.Summary.Cell>
                        )}
                        {showDailyAdjustment && (
                          <>
                            <Table.Summary.Cell index={3} align="right" />
                            <Table.Summary.Cell index={4} align="right">
                              <Money value={rows.reduce((a, r) => a + r.daily_class_adjustment, 0)} />
                            </Table.Summary.Cell>
                          </>
                        )}
                      </Table.Summary.Row>
                    </Table.Summary>
                  ) : null
                }
              />
            </div>
          )}

          {isPureGroupClasses && cancelledMeetings.length > 0 && (
            <div>
              <div className="font-semibold mb-2">שיעורים שבוטלו</div>
              <Table<CancelledMeetingDetail>
                rowKey={(_, i) => String(i)}
                size="small"
                columns={cancelledColumns}
                dataSource={cancelledMeetings}
                sticky={STICKY_IN_DRAWER}
                scroll={{ x: columnsWidth(cancelledColumns) }}
                pagination={false}
              />
            </div>
          )}

          {isPureGroupClasses && conversions.length > 0 && (
            <div>
              <div className="font-semibold mb-2">המרות לקוחות מניסיון</div>
              {detail?.conversion_tier && (
                <div className="mb-2 text-secondary-text">
                  {conversionTierText(detail.conversion_tier, conversions.length)} · סה״כ{' '}
                  <Money value={comp?.conversion_bonus ?? 0} />
                </div>
              )}
              <Table<ConversionDetail>
                rowKey={(_, i) => String(i)}
                size="small"
                columns={conversionColumns}
                dataSource={conversions}
                sticky={STICKY_IN_DRAWER}
                scroll={{ x: columnsWidth(conversionColumns) }}
                pagination={false}
              />
            </div>
          )}

          <div>
            <div className="font-semibold mb-2">פירוט רכיבי השכר</div>
            <div className="border rounded">
              {componentLines.length === 0 && (
                <div className="px-3 py-2 text-secondary-text">אין רכיבי שכר בתקופה זו.</div>
              )}
              {componentLines.map((l) => (
                <div key={l.label} className="flex justify-between px-3 py-2 border-b last:border-b-0">
                  <span>{l.label}</span>
                  <Money value={l.value} />
                </div>
              ))}
              {comp && wh && wh.withholding > 0 && (
                <>
                  <div className="flex justify-between px-3 py-2 border-b">
                    <span>סה"כ חשבונית</span>
                    <Money value={comp.total} />
                  </div>
                  <div className="flex justify-between px-3 py-2 border-b text-error-red">
                    <span>{`ניכוי מס במקור (${formatWithholdingPct(wh.withholding_pct)})`}</span>
                    <span>
                      -<Money value={wh.withholding} />
                    </span>
                  </div>
                </>
              )}
              {comp && wh && (
                <div className="flex justify-between px-3 py-2 font-semibold bg-grey-bg">
                  <span>{wh.withholding > 0 ? 'סה"כ להעברה' : 'סה"כ לתשלום'}</span>
                  <Money value={wh.withholding > 0 ? wh.net_total : comp.total} />
                </div>
              )}
            </div>

            {/* What the coach's uploaded חשבונית actually says, when the box
                verifies it — sits directly under the totals it's compared to. */}
            {invoiceCheckStatus && (
              <div
                className={`mt-2 text-small ${
                  invoiceCheckStatus === 'mismatch' ? 'text-error-red' : 'text-secondary-text'
                }`}
              >
                {INVOICE_CHECK_LABELS[invoiceCheckStatus]}
                {invoiceCheckStatus !== 'unreadable' && invoiceAmount != null && (
                  <> — בחשבונית שצורפה: <Money value={invoiceAmount} /></>
                )}
                {invoiceCheckIssues.length > 0 && (
                  <ul className="list-disc ps-5 mt-1">
                    {invoiceCheckIssues.map((i) => (
                      <li key={i.field}>{describeInvoiceIssue(i)}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}
